import cn from 'clsx';
import React from 'react';

import { Button, Heading1, Heading2, Heading4, Image, useDeviceType } from '@cian/ui-kit';
import { NBSP } from 'shared/constants/common';
import { ETestIds } from 'shared/constants/testIds';
import { Breadcrumbs, IBreadcrumbNavItem } from '../Breadcrumbs';
import { ContainerInner } from '../ContainerInner';
import { FeatureHighlightList } from '../FeatureHighlightList';
import styles from './styles.css';

const PRESENTATION_BANK_IMAGE_URL =
  'https://static.cdn-cian.ru/frontend/mortgage-assets/mortgage_promo_frontend/bank.webp';
const PRESENTATION_PERCENT_IMAGE_URL =
  'https://static.cdn-cian.ru/frontend/mortgage-assets/mortgage_promo_frontend/percent.webp';

const STEPS = [
  { title: 'Выберите\nбанки', description: `Рассчитайте и${NBSP}выберите выгодные${NBSP}предложения` },
  { title: 'Заполните\nанкету', description: 'Займёт 5–7 минут' },
  { title: 'Ожидайте\nодобрения', description: `Позвоним или пришлём смс в${NBSP}течение${NBSP}24${NBSP}часов` },
  { title: 'Оформите\nсделку', description: `Мы${NBSP}будем рядом на каждом этапе` },
];

interface INewPresentationBlockProps {
  breadcrumbs: IBreadcrumbNavItem[];
  onCtaClick: () => void;
}

export const NewPresentationBlock: React.FC<INewPresentationBlockProps> = ({ onCtaClick, breadcrumbs }) => {
  const deviceType = useDeviceType();

  const Subtitle = deviceType === 'desktop' ? Heading2 : Heading4;

  const hasBreadcrumbs = breadcrumbs.length > 1;

  const items = STEPS.map((step, index) => ({
    ...step,
    bullet:
      deviceType === 'phone' ? (
        <Heading2 fontWeight="700">{index + 1}</Heading2>
      ) : (
        <Heading1 fontWeight="500">{index + 1}</Heading1>
      ),
  }));

  return (
    <ContainerInner>
      <div className={cn(styles['container'], styles[deviceType], hasBreadcrumbs && styles['with-breadcrumbs'])}>
        {hasBreadcrumbs && (
          <div className={cn(styles['breadcrumbs-container'], styles[deviceType])}>
            <Breadcrumbs
              items={breadcrumbs.map((item, index) =>
                index === 0 ? { ...item, color: 'text-secondary-disabled' } : item,
              )}
            />
          </div>
        )}
        <div className={cn(styles['main'], styles[deviceType])} data-testid={ETestIds.PresentationBlock}>
          <div className={styles['logo']}>
            <div className={styles['logo-card']}>
              <Image src={PRESENTATION_BANK_IMAGE_URL} height={120} objectFit="contain" preloader={false} />
            </div>
            <div className={styles['logo-percent']}>
              <Image src={PRESENTATION_PERCENT_IMAGE_URL} height={76} objectFit="contain" preloader={false} />
            </div>
          </div>
          <div className={cn(styles['content'], styles[deviceType])}>
            <div className={cn(styles['title'], styles[deviceType])}>
              <Heading1 fontWeight="700">Подберите ипотеку</Heading1>
            </div>
            <Subtitle color="text-secondary-default" fontWeight="700">
              Покажем{NBSP}предложения, подходящие{NBSP}именно{NBSP}вам
            </Subtitle>
          </div>
          <div className={styles['cta']}>
            <Button size="L" onClick={onCtaClick} data-testid={ETestIds.PresentationBtn}>
              Подобрать
            </Button>
          </div>
        </div>
        <div className={cn(styles['highlights'], styles[deviceType])}>
          <FeatureHighlightList
            items={items}
            testId={ETestIds.PresentationStepsBlock}
            itemTestIdPrefix={ETestIds.PresentationStepItem}
          />
        </div>
      </div>
    </ContainerInner>
  );
};
